import React, { useState, useRef, MutableRefObject, useMemo, useContext } from 'react';
import * as S from './SAMLIdentityProviderDetail.styles';
import RequiredMark from 'components/RequiredMark/RequiredMark';
import ReactTooltip from 'react-tooltip';
import { IdentityProviderService } from 'services';
import { Notification, NotificationService } from 'services/notification-service';
import { UrlValidator } from 'helpers/validator/validator';
import { FiletoBase64 } from 'helpers/converter/converter';
import { useTranslation, Trans } from 'react-i18next';
import { SamlIndetityProvider, SAML_EMAIL_ATTRIBUTE } from 'models/identity-provider/identity-provider';
import { ClientFactory } from 'services';
import { IntegrationContext } from 'helpers/integration/IntegrationContext';

interface ISAMLIdentityProviderDetail {
    update: () => void;
}

export default function SAMLIdentityProviderDetail({ update }: ISAMLIdentityProviderDetail): JSX.Element {

    const { t } = useTranslation();
    const integration = useContext(IntegrationContext);
    const fileUpload = useRef() as MutableRefObject<HTMLDivElement>;
    const [uploadFileDisabled, setUploadFileDisabled] = useState(false);
    const [uploadFileName, setUploadFileName] = useState('');
    const [emailAttribute] = useState(SAML_EMAIL_ATTRIBUTE);
    const [configurationName] = useState('');
    const client = useMemo(() => ClientFactory.createClient(integration.getTenantId(), integration.getIntegrationType(), integration.getSessionHeaders()), [integration]);
    const identityProviderService: IdentityProviderService = useMemo(() => new IdentityProviderService(client), [client]);
    const uploadNotification: Notification = new Notification(t('notifications:NOTIFICATION_IDP_ADD_LOADING'), 'identity-provider-notification', { isLoading: true });

    const resetUploadFileName = (): void => {
        (fileUpload.current as HTMLInputElement).value = '';
        setUploadFileName('');
    };

    const [provider, setProvider] = useState({
        metadata: null as unknown as File,
        endpointURL: '',
        name: '',
        emailAttribute: emailAttribute,
        configurationName: ''
    });

    const onFileUploadHandler = (e: React.SyntheticEvent) => {
        const target = e.currentTarget as HTMLInputElement;
        const prop = { metadata: null as unknown as File };
        if (target.files !== null && target.files.length !== 0) {
            prop.metadata = target.files[0];
            setUploadFileName(prop.metadata.name);
        } else {
            resetUploadFileName();
        }

        setProvider({ ...provider, metadata: prop.metadata });
    };
    const onMetadataURLHandler = (e: React.SyntheticEvent) => {
        const target = e.currentTarget as HTMLInputElement;
        setUploadFileDisabled(target.value !== '');
        if (target.value !== '') resetUploadFileName();
        setProvider({ ...provider, endpointURL: target.value, metadata: null as unknown as File });
    };
    const onEmailAttributeHandler = (e: React.SyntheticEvent) => {
        const target = e.currentTarget as HTMLInputElement;
        setProvider({ ...provider, emailAttribute: target.value });
    };
    const onConfigurationNameHandler = (e: React.SyntheticEvent) => {
        const target = e.currentTarget as HTMLInputElement;
        setProvider({ ...provider, configurationName: target.value });
    };
    const onFileUploadButtonHandler = (e: React.SyntheticEvent) => {
        e.preventDefault();
        fileUpload.current.click();
    };

    const postSamlForm = async () => {

        uploadNotification.show();
        const samlIdp: SamlIndetityProvider = new SamlIndetityProvider(provider.emailAttribute);

        if (provider.endpointURL) {
            samlIdp.setMedataURL(provider.endpointURL);
        } else if (provider.metadata) {
            samlIdp.setMedataFile(await FiletoBase64(provider.metadata));
        } else {
            return updateError(t('notifications:NOTIFICATION_IDP_ADD_FAILED'));
        }

        samlIdp.setConfigurationName(provider.configurationName);

        try {
            await identityProviderService.createIdentityProvider(samlIdp.getData());
            return updateSuccessfully();
        } catch(error) {
            const errorMessage = (error instanceof Error && error.message) || t('notifications:NOTIFICATION_IDP_ADD_FAILED');
            updateError(errorMessage);
        }
    };

    const updateSuccessfully = () => {
        uploadNotification.update({ render: t('notifications:NOTIFICATION_IDP_ADD_SUCCEED'), type: 'success' });
        update();
    };

    const updateError = (message: string) => {
        uploadNotification.update({ render: message, type: 'error', isLoading: false });
    };

    const showErrorMessage = (message: string) => {
        NotificationService.getInstance().notifyError(message);
    };

    const validateForm = () => {
        if (provider.configurationName === '') {
            showErrorMessage(t('errors:INVALID_CONFIGURATION_NAME'));
            return false;
        }

        if (provider.metadata === null && provider.endpointURL === '') {
            showErrorMessage(t('errors:METADATA_OR_URL'));
            return false;
        }

        if (provider.metadata === null && !UrlValidator(provider.endpointURL)) {
            showErrorMessage(t('errors:INVALID_ENDPOINT'));
            return false;
        }

        if (provider.metadata && provider.metadata.type !== 'text/xml') {
            showErrorMessage(t('errors:INVALID_XML_TYPE'));
            return false;
        }

        if (provider.emailAttribute === '') {
            showErrorMessage(t('errors:PROVIDER_ATTRIBUTE'));
            return false;
        }
        return true;
    };

    // eslint-disable-next-line
    const submit = () => {
        if (validateForm()) {
            postSamlForm();
        }
    };

    React.useEffect(() => {
        window.addEventListener('samlFormSubmit', submit);
        return () => {
            window.removeEventListener('samlFormSubmit', submit);
        };
    }, [submit]);

    return (
        <>
            <ReactTooltip place="top" type="dark" effect="solid" event="click focus" eventOff="blur" />
            <S.Card>
                <S.CardTitle><Trans>SAML.FORM_TITLE</Trans><S.Required><RequiredMark /> Required</S.Required></S.CardTitle>
                <S.CardBody>
                    <S.Label>
                        <Trans>SAML.CONFIGURATION_NAME</Trans>
                        <RequiredMark />
                    </S.Label>
                    <S.InputGroup>
                        <S.TextInput
                            role="configuration-name"
                            defaultValue={configurationName}
                            onChange={onConfigurationNameHandler}
                        />
                        <S.QMARK size="18" data-tip="" visibility="hidden"></S.QMARK>
                    </S.InputGroup>
                    <S.Label>
                        <Trans>SAML.METADATA_LABEL</Trans>
                        <RequiredMark />
                    </S.Label>
                    <S.InputGroup>
                        <S.FileButton disabled={uploadFileDisabled} onClick={onFileUploadButtonHandler}>
                            {uploadFileName === ''
                                ? <S.Span>{t('SAML.SELECT_FILE')}</S.Span>
                                : <S.Span>{uploadFileName}</S.Span>}
                        </S.FileButton>
                        <S.FileInput
                            ref={fileUpload}
                            role="metadata-file"
                            type='file'
                            onChange={onFileUploadHandler}
                            accept="text/xml"
                            hidden
                        />
                        <S.OR>OR</S.OR>
                        <S.TextInput
                            role="metadata-endpoint-url"
                            value={provider.endpointURL}
                            onChange={onMetadataURLHandler}
                            placeholder={t('SAML.METADATA_ENDPOINT_URL_PLACEHOLDER')}
                        />
                        <S.QMARK size="23" data-tip={t('SAML.METADATA_LABEL_TIP')}></S.QMARK>
                    </S.InputGroup>
                    <S.Label>
                        <Trans>SAML.EMAIL_ATTRIBUTE_LABEL</Trans>
                        <RequiredMark />
                    </S.Label>
                    <S.InputGroup>
                        <S.TextInput
                            defaultValue={emailAttribute}
                            onChange={onEmailAttributeHandler}
                        />
                        <S.QMARK size="18" data-tip={t('SAML.EMAIL_ATTRIBUTE_LABEL_TIP')}></S.QMARK>
                    </S.InputGroup>
                </S.CardBody>
            </S.Card>
        </>
    );
}
